import { useEffect, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Strategy, SpinnerGap, FilePdf, Clock, Fire, Knife, Leaf,
  CheckCircle, MagnifyingGlass, Lightbulb, Plus, Trash, PencilSimple, CheckSquare,
  FloppyDisk, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHASE_CONFIG = {
  1: { icon: Clock, color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", label: "Fase 1", title: "Tarefas Longas e Passivas" },
  2: { icon: Fire, color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200", label: "Fase 2", title: "Cocção Específica e Descanso" },
  3: { icon: Knife, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Fase 3", title: "Mise en Place Pesado" },
  4: { icon: Leaf, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", label: "Fase 4", title: "Finalização e Mise en Place Leve" },
};

const EMPTY_PLAN = {
  phases: [
    { phase: 1, title: "Tarefas Longas e Passivas", icon: "clock", tasks: [] },
    { phase: 2, title: "Cocção Específica e Descanso", icon: "fire", tasks: [] },
    { phase: 3, title: "Mise en Place Pesado", icon: "knife", tasks: [] },
    { phase: 4, title: "Finalização e Mise en Place Leve", icon: "leaf", tasks: [] },
  ],
  total_estimated_time: "",
  pro_tips: [],
};

export default function AttackPlan() {
  const [sheets, setSheets] = useState([]);
  const [events, setEvents] = useState([]);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planSheets, setPlanSheets] = useState([]);
  const [checkedTasks, setCheckedTasks] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [mode, setMode] = useState("ai"); // "ai" or "manual"
  const [manualPlan, setManualPlan] = useState(null);
  const [addTaskDialog, setAddTaskDialog] = useState(false);
  const [addTaskPhase, setAddTaskPhase] = useState(1);
  const [newTask, setNewTask] = useState({ task: "", time: "", sheet: "", tip: "" });
  const [editingTask, setEditingTask] = useState(null);
  const [sourceTab, setSourceTab] = useState("sheets"); // "sheets" | "events"
  const [savedPlans, setSavedPlans] = useState([]);
  const [activeTab, setActiveTab] = useState("new"); // "new" | "saved"
  const [savingPlan, setSavingPlan] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sheetsRes, eventsRes, plansRes] = await Promise.all([
          axios.get(`${API}/technical-sheets`),
          axios.get(`${API}/events`).catch(() => ({ data: [] })),
          axios.get(`${API}/attack-plans`).catch(() => ({ data: [] })),
        ]);
        setSheets(sheetsRes.data);
        setEvents(eventsRes.data || []);
        setSavedPlans(plansRes.data || []);
      } catch (e) { toast.error("Erro ao carregar dados"); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const savePlan = async () => {
    const activePlanData = plan || manualPlan;
    if (!activePlanData) { toast.error("Nenhum plano para salvar"); return; }
    setSavingPlan(true);
    try {
      const title = `Plano — ${planSheets.join(", ").substring(0, 50)}`;
      const res = await axios.post(`${API}/attack-plans`, {
        title, plan: activePlanData, sheets: planSheets, notes,
      });
      toast.success("Plano salvo!");
      setSavedPlans(prev => [{ id: res.data.id, title, plan: activePlanData, sheets: planSheets, notes, created_at: new Date().toISOString() }, ...prev]);
    } catch { toast.error("Erro ao salvar plano"); }
    finally { setSavingPlan(false); }
  };

  const loadSavedPlan = (saved) => {
    setPlan(saved.plan);
    setPlanSheets(saved.sheets || []);
    setNotes(saved.notes || "");
    setCheckedTasks({});
    setManualPlan(null);
    setActiveTab("new");
    toast.success(`Plano "${saved.title}" carregado!`);
  };

  const deleteSavedPlan = async (planId) => {
    try {
      await axios.delete(`${API}/attack-plans/${planId}`);
      setSavedPlans(prev => prev.filter(p => p.id !== planId));
      toast.success("Plano removido");
    } catch { toast.error("Erro ao remover"); }
  };

  const toggleSheet = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // When an event is selected, auto-select its sheets
  const selectEvent = (eventId) => {
    setSelectedEventId(eventId);
    if (eventId) {
      const ev = events.find(e => e.id === eventId);
      if (ev?.sheets) {
        const sheetIds = ev.sheets.map(s => s.sheet_id).filter(Boolean);
        setSelectedIds(sheetIds);
        setNotes(ev.notes || `Evento: ${ev.name} — ${ev.num_guests || '?'} convidados`);
      }
    }
  };

  const handleGenerate = async () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos uma ficha técnica");
      return;
    }
    setGenerating(true);
    setPlan(null);
    setCheckedTasks({});
    try {
      const res = await axios.post(`${API}/attack-plan/generate`, {
        sheet_ids: selectedIds,
        notes: notes || null,
      });
      if (res.data.plan) {
        setPlan(res.data.plan);
        setPlanSheets(res.data.sheets || []);
        toast.success("Plano de Ataque gerado pelo Sous-Chef IA!");
      } else {
        toast.error("Formato inesperado. Tente novamente.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar plano");
    } finally {
      setGenerating(false);
    }
  };

  const handleStartManual = () => {
    const selected = sheets.filter((s) => selectedIds.includes(s.id));
    setPlanSheets(selected.map((s) => s.name));
    setManualPlan(JSON.parse(JSON.stringify(EMPTY_PLAN)));
    setPlan(null);
    setCheckedTasks({});
    toast.success("Plano manual iniciado — adicione tarefas em cada fase!");
  };

  const handleAddTask = () => {
    if (!newTask.task.trim()) { toast.error("Descreva a tarefa"); return; }
    const target = manualPlan || plan;
    if (!target) return;
    const updated = JSON.parse(JSON.stringify(target));
    const phase = updated.phases.find((p) => p.phase === addTaskPhase);
    if (phase) {
      if (editingTask !== null) {
        phase.tasks[editingTask.taskIdx] = { ...newTask };
      } else {
        phase.tasks.push({ ...newTask, tip: newTask.tip || null });
      }
    }
    if (manualPlan) setManualPlan(updated); else setPlan(updated);
    setNewTask({ task: "", time: "", sheet: "", tip: "" });
    setEditingTask(null);
    setAddTaskDialog(false);
  };

  const handleDeleteTask = (phaseNum, taskIdx) => {
    const target = manualPlan || plan;
    if (!target) return;
    const updated = JSON.parse(JSON.stringify(target));
    const phase = updated.phases.find((p) => p.phase === phaseNum);
    if (phase) phase.tasks.splice(taskIdx, 1);
    if (manualPlan) setManualPlan(updated); else setPlan(updated);
    const key = `${phaseNum}-${taskIdx}`;
    setCheckedTasks((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const openEditTask = (phaseNum, taskIdx) => {
    const target = manualPlan || plan;
    const task = target.phases.find((p) => p.phase === phaseNum)?.tasks[taskIdx];
    if (!task) return;
    setNewTask({ task: task.task, time: task.time || "", sheet: task.sheet || "", tip: task.tip || "" });
    setAddTaskPhase(phaseNum);
    setEditingTask({ phaseIdx: phaseNum, taskIdx });
    setAddTaskDialog(true);
  };

  const toggleTask = (phaseIdx, taskIdx) => {
    const key = `${phaseIdx}-${taskIdx}`;
    setCheckedTasks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const activePlan = plan || manualPlan;
  const totalTasks = activePlan ? activePlan.phases.reduce((s, p) => s + p.tasks.length, 0) : 0;
  const completedTasks = Object.values(checkedTasks).filter(Boolean).length;
  const isEditable = manualPlan !== null || plan !== null;

  // ========== CONVERT TO CHECKLIST ==========
  const convertToChecklist = async () => {
    if (!activePlan) return;
    const phaseLabels = { 1: "Tarefas Longas e Passivas", 2: "Cocção e Descanso", 3: "Mise en Place", 4: "Finalização" };
    const items = [];
    for (const phase of activePlan.phases) {
      for (const task of phase.tasks) {
        items.push({
          id: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`,
          text: `[${phaseLabels[phase.phase] || `Fase ${phase.phase}`}] ${task.task}${task.time ? ` (${task.time})` : ""}`,
          checked: false,
          category: phaseLabels[phase.phase] || `Fase ${phase.phase}`,
        });
      }
    }
    if (items.length === 0) { toast.error("Plano sem tarefas para converter"); return; }
    try {
      const name = `Plano de Ataque — ${planSheets.join(", ").substring(0, 40)}`;
      await axios.post(`${API}/checklists`, { name, checklist_type: "production", items });
      toast.success("Checklist de produção criada!");
      navigate("/checklists");
    } catch { toast.error("Erro ao criar checklist"); }
  };


  // ========== PDF EXPORT ==========
  const exportPDF = () => {
    if (!activePlan) return;
    const doc = new jsPDF();
    const mL = 15, mR = 195, maxY = 275;
    let y = 15;
    const ck = (n) => { if (y + n > maxY) { doc.addPage(); y = 15; } };

    doc.setFontSize(16); doc.setFont(undefined, "bold");
    doc.text("PLANO DE ATAQUE", mL, y); y += 6;
    doc.setFontSize(8); doc.setFont(undefined, "normal"); doc.setTextColor(100);
    doc.text(`Fichas: ${planSheets.join(", ")}`, mL, y); y += 4;
    doc.text(`Gerado em ${new Date().toLocaleDateString("pt-BR")}${activePlan.total_estimated_time ? ` | Tempo estimado: ${activePlan.total_estimated_time}` : ""}`, mL, y);
    doc.setTextColor(0); y += 3;
    doc.setLineWidth(0.8); doc.line(mL, y, mR, y); y += 8;

    activePlan.phases.forEach((phase) => {
      if (phase.tasks.length === 0) return;
      ck(15);
      doc.setFontSize(11); doc.setFont(undefined, "bold");
      doc.setFillColor(240, 240, 240);
      doc.rect(mL, y - 5, mR - mL, 8, "F");
      doc.text(`FASE ${phase.phase}: ${phase.title.toUpperCase()}`, mL + 3, y); y += 8;
      doc.setFontSize(8);
      phase.tasks.forEach((task) => {
        ck(14); doc.setFont(undefined, "normal");
        doc.setDrawColor(150); doc.setLineWidth(0.3); doc.rect(mL + 1, y - 3, 3.5, 3.5);
        const tl = doc.splitTextToSize(task.task, 145);
        doc.text(tl, mL + 7, y); y += tl.length * 3.5;
        doc.setTextColor(120); doc.setFontSize(7);
        let meta = "";
        if (task.time) meta += `Tempo: ${task.time}`;
        if (task.sheet) meta += `${meta ? "  |  " : ""}Ficha: ${task.sheet}`;
        if (meta) { doc.text(meta, mL + 7, y); y += 3; }
        if (task.tip) {
          doc.setFont(undefined, "italic");
          const tipL = doc.splitTextToSize(`Dica: ${task.tip}`, 145);
          doc.text(tipL, mL + 7, y); y += tipL.length * 3; doc.setFont(undefined, "normal");
        }
        doc.setTextColor(0); doc.setFontSize(8); y += 3;
      });
      y += 4;
    });

    if (activePlan.pro_tips?.length > 0) {
      ck(15); doc.setFontSize(9); doc.setFont(undefined, "bold");
      doc.text("DICAS DO SOUS-CHEF:", mL, y); y += 5;
      doc.setFontSize(7); doc.setFont(undefined, "normal");
      activePlan.pro_tips.forEach((tip) => {
        ck(6); const l = doc.splitTextToSize(`- ${tip}`, mR - mL);
        doc.text(l, mL, y); y += l.length * 3.5;
      });
    }

    const pages = doc.internal.pages.length - 1;
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i); doc.setFontSize(7); doc.setDrawColor(180); doc.setLineWidth(0.3);
      doc.line(mL, 283, mR, 283); doc.setFont(undefined, "bold");
      doc.text("SOP & RAFT — Plano de Ataque", mL, 287);
      doc.setFont(undefined, "normal"); doc.text(`${i}/${pages}`, mR, 287, { align: "right" });
    }
    doc.save(`Plano_de_Ataque_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF exportado!");
  };

  const filteredSheets = sheets.filter((s) => s.name.toLowerCase().includes(searchQuery.toLowerCase()));

  if (loading) return <div className="text-center py-12 text-zinc-500">Carregando...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">Plano de Ataque</h2>
          <p className="text-sm text-zinc-500 mt-1">Cronograma de producao — gerado pela IA ou montado por voce</p>
        </div>
        {activePlan && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={savePlan} disabled={savingPlan} data-testid="save-plan-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              {savingPlan ? <SpinnerGap size={14} className="mr-1 animate-spin" /> : <FloppyDisk size={14} className="mr-1" />}
              Salvar Plano
            </Button>
          </div>
        )}
      </div>

      {/* Tabs: Novo Plano | Salvos */}
      <div className="flex gap-1 bg-zinc-100 p-1 rounded-lg w-fit">
        <button onClick={() => setActiveTab("new")} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === "new" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500 hover:text-zinc-700"}`} data-testid="tab-new-plan">
          Novo Plano
        </button>
        <button onClick={() => setActiveTab("saved")} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${activeTab === "saved" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500 hover:text-zinc-700"}`} data-testid="tab-saved-plans">
          Salvos {savedPlans.length > 0 && <span className="bg-zinc-200 text-zinc-600 text-xs px-1.5 py-0.5 rounded-full">{savedPlans.length}</span>}
        </button>
      </div>

      {/* Tab: Saved Plans */}
      {activeTab === "saved" && (
        <div className="space-y-3">
          {savedPlans.length === 0 ? (
            <Card className="border-zinc-200 shadow-sm rounded-sm">
              <div className="text-center py-12 text-zinc-400">
                <Strategy size={40} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm">Nenhum plano salvo ainda</p>
                <p className="text-xs mt-1">{`Gere um plano e clique em "Salvar Plano" para revisita-lo depois`}</p>
              </div>
            </Card>
          ) : (
            savedPlans.map((sp) => (
              <Card key={sp.id} className="border-zinc-200 shadow-sm rounded-sm hover:border-zinc-300 transition-colors">
                <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm text-zinc-900 truncate">{sp.title}</h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <span className="text-[10px] text-zinc-400">{new Date(sp.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      {sp.plan?.phases && <span className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded">{sp.plan.phases.reduce((s, p) => s + (p.tasks?.length || 0), 0)} tarefas</span>}
                      {sp.plan?.total_estimated_time && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{sp.plan.total_estimated_time}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => loadSavedPlan(sp)} data-testid={`load-plan-${sp.id}`}>
                      <Eye size={14} className="mr-1" /> Abrir
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs border-rose-200 text-rose-500 hover:bg-rose-50" onClick={() => deleteSavedPlan(sp.id)} data-testid={`delete-plan-${sp.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Tab: New Plan */}
      {activeTab === "new" && <>

      {/* Source Selection */}
      {!activePlan && (
        <Card className="border-zinc-200 shadow-sm rounded-sm">
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-lg font-semibold tracking-tight text-zinc-900">
                Selecione a Origem
              </CardTitle>
              <span className="text-xs text-zinc-500 bg-zinc-100 px-2.5 py-1 rounded-full">
                {selectedIds.length} ficha(s)
              </span>
            </div>
            {/* Tabs: Fichas / Eventos */}
            <div className="flex border-b border-zinc-200 -mb-2 mt-2">
              <button onClick={() => setSourceTab("sheets")}
                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${sourceTab === "sheets" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
                data-testid="source-tab-sheets">
                Fichas Técnicas
              </button>
              {events.length > 0 && (
                <button onClick={() => setSourceTab("events")}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${sourceTab === "events" ? "border-violet-600 text-violet-700" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
                  data-testid="source-tab-events">
                  Eventos ({events.length})
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Events selection */}
            {sourceTab === "events" && (
              <div className="space-y-2 mb-4">
                {events.map(ev => (
                  <div key={ev.id} role="button" tabIndex={0} onClick={() => selectEvent(ev.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedEventId === ev.id ? 'border-violet-500 bg-violet-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                    data-testid={`event-select-${ev.id}`}>
                    <Strategy size={20} className={selectedEventId === ev.id ? "text-violet-600" : "text-zinc-400"} weight="fill" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{ev.name}</p>
                      <p className="text-[10px] text-zinc-500">
                        {ev.event_date ? new Date(ev.event_date).toLocaleDateString("pt-BR") : ""} 
                        {ev.num_guests ? ` · ${ev.num_guests} convidados` : ""} 
                        {ev.sheets ? ` · ${ev.sheets.length} fichas` : ""}
                      </p>
                    </div>
                    {selectedEventId === ev.id && <CheckCircle size={20} className="text-violet-600" weight="fill" />}
                  </div>
                ))}
                {selectedEventId && <p className="text-xs text-violet-600 mt-2">{selectedIds.length} ficha(s) do evento selecionada(s) automaticamente</p>}
              </div>
            )}

            {/* Sheets selection */}
            {sourceTab === "sheets" && (
              <>
                <div className="relative mb-4">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input data-testid="attack-plan-search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Buscar ficha técnica..." className="pl-9" />
                </div>
                {filteredSheets.length === 0 ? (
                  <p className="text-center text-sm text-zinc-400 py-6">Nenhuma ficha encontrada.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[300px] overflow-y-auto pr-1">
                    {filteredSheets.map((sheet) => {
                      const selected = selectedIds.includes(sheet.id);
                      return (
                        <div key={sheet.id} role="button" tabIndex={0} onClick={() => toggleSheet(sheet.id)}
                      onKeyDown={(e) => e.key === "Enter" && toggleSheet(sheet.id)}
                      className={`text-left p-3 rounded-lg border-2 transition-all cursor-pointer ${selected ? "border-zinc-900 bg-zinc-50 ring-1 ring-zinc-900" : "border-zinc-200 hover:border-zinc-400"}`}
                      data-testid={`select-sheet-${sheet.id}`}>
                      <div className="flex items-start gap-2.5">
                        <Checkbox checked={selected} className="mt-0.5 pointer-events-none" tabIndex={-1} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-zinc-900 truncate">{sheet.name}</p>
                          <p className="text-xs text-zinc-500 mt-0.5">{sheet.yield_quantity} {sheet.yield_unit} • {sheet.ingredients?.length || 0} ingr.</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
              </>
            )}

            <div className="mt-4">
              <Input data-testid="attack-plan-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Observações (opcional)... Ex: Servir às 19h, equipe de 3 pessoas" className="text-sm" />
            </div>

            {/* Two action buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <Button onClick={handleGenerate} disabled={selectedIds.length === 0 || generating}
                className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="generate-attack-plan-btn">
                {generating ? (
                  <><SpinnerGap size={18} className="mr-2 animate-spin" /> Gerando...</>
                ) : (
                  <><Strategy size={18} className="mr-2" weight="fill" /> Gerar com Sous-Chef IA</>
                )}
              </Button>
              <Button onClick={handleStartManual} disabled={selectedIds.length === 0} variant="outline"
                data-testid="manual-attack-plan-btn">
                <PencilSimple size={18} className="mr-2" /> Criar Manualmente
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Plan (AI or Manual) */}
      {activePlan && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-xl font-bold text-zinc-900">
                {manualPlan ? "Plano Manual" : "Plano Gerado pelo Sous-Chef IA"}
              </h3>
              <p className="text-sm text-zinc-500">
                {completedTasks}/{totalTasks} tarefas • Fichas: {planSheets.join(", ")}
                {activePlan.total_estimated_time && ` • ${activePlan.total_estimated_time}`}
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={convertToChecklist} variant="outline" data-testid="convert-to-checklist-btn" disabled={totalTasks === 0}>
                <CheckSquare size={18} className="mr-2 text-emerald-600" /> Checklist
              </Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-attack-plan-pdf" disabled={totalTasks === 0}>
                <FilePdf size={18} className="mr-2 text-red-600" /> PDF
              </Button>
              <Button onClick={() => { setPlan(null); setManualPlan(null); setCheckedTasks({}); }} variant="ghost" className="text-zinc-500">
                Novo Plano
              </Button>
            </div>
          </div>

          {/* Progress */}
          <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
            <div className="h-full bg-zinc-900 transition-all duration-500 ease-out rounded-full"
              style={{ width: `${totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0}%` }} />
          </div>

          {/* Phases */}
          {activePlan.phases.map((phase) => {
            const config = PHASE_CONFIG[phase.phase] || PHASE_CONFIG[1];
            const PhaseIcon = config.icon;
            return (
              <Card key={phase.phase} className={`border-2 ${config.border} shadow-sm rounded-sm`}>
                <CardHeader className={`${config.bg} py-3`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <CardTitle className={`text-sm font-bold uppercase tracking-wider ${config.color} flex items-center gap-2`}>
                      <PhaseIcon size={18} weight="fill" />
                      {config.label}: {phase.title}
                    </CardTitle>
                    <Button size="sm" variant="ghost" className={`h-7 text-xs ${config.color}`}
                      onClick={() => { setAddTaskPhase(phase.phase); setEditingTask(null); setNewTask({ task: "", time: "", sheet: "", tip: "" }); setAddTaskDialog(true); }}
                      data-testid={`add-task-phase-${phase.phase}`}>
                      <Plus size={14} className="mr-1" /> Tarefa
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {phase.tasks.length === 0 && (
                    <p className="text-center text-xs text-zinc-400 py-4">Nenhuma tarefa nesta fase</p>
                  )}
                  {phase.tasks.map((task, taskIdx) => {
                    const key = `${phase.phase}-${taskIdx}`;
                    const done = checkedTasks[key];
                    return (
                      <div key={key} className={`flex items-start gap-3 px-4 py-3 border-b border-zinc-100 last:border-0 transition-colors ${done ? "bg-zinc-50" : "hover:bg-zinc-50/50"}`}>
                        <button type="button" onClick={() => toggleTask(phase.phase, taskIdx)} className="mt-0.5 shrink-0" data-testid={`task-${key}`}>
                          {done ? <CheckCircle size={20} className="text-emerald-600" weight="fill" /> : <div className="w-5 h-5 rounded-full border-2 border-zinc-300" />}
                        </button>
                        <div className={`flex-1 min-w-0 ${done ? "opacity-50" : ""}`}>
                          <p className={`text-sm ${done ? "line-through text-zinc-500" : "text-zinc-900"}`}>{task.task}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {task.time && <span className="text-[10px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">⏱ {task.time}</span>}
                            {task.sheet && <span className="text-[10px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">📋 {task.sheet}</span>}
                          </div>
                          {task.tip && (
                            <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mt-1.5 flex items-start gap-1">
                              <Lightbulb size={12} className="shrink-0 mt-0.5" weight="fill" />{task.tip}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button type="button" onClick={() => openEditTask(phase.phase, taskIdx)} className="text-zinc-400 hover:text-zinc-600 p-1">
                            <PencilSimple size={14} />
                          </button>
                          <button type="button" onClick={() => handleDeleteTask(phase.phase, taskIdx)} className="text-zinc-400 hover:text-red-500 p-1">
                            <Trash size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}

          {/* Pro tips */}
          {activePlan.pro_tips?.length > 0 && (
            <Card className="border-amber-200 bg-amber-50 shadow-sm rounded-sm">
              <CardContent className="py-4">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-2">Dicas do Sous-Chef</p>
                <ul className="space-y-1.5">
                  {activePlan.pro_tips.map((tip, idx) => (
                    <li key={`tip-${idx}`} className="text-sm text-amber-900 flex items-start gap-2">
                      <Lightbulb size={14} className="shrink-0 mt-0.5 text-amber-600" weight="fill" />{tip}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Add/Edit Task Dialog */}
      <Dialog open={addTaskDialog} onOpenChange={setAddTaskDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingTask ? "Editar Tarefa" : "Adicionar Tarefa"}</DialogTitle>
            <DialogDescription className="sr-only">Tarefa do plano de ataque</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Fase</Label>
              <Select value={String(addTaskPhase)} onValueChange={(v) => setAddTaskPhase(Number(v))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>{PHASE_CONFIG[n].label}: {PHASE_CONFIG[n].title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Descrição da Tarefa *</Label>
              <Textarea value={newTask.task} onChange={(e) => setNewTask({ ...newTask, task: e.target.value })}
                placeholder="Descreva a tarefa detalhadamente..." className="mt-1 min-h-[80px]" data-testid="new-task-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Tempo Estimado</Label>
                <Input value={newTask.time} onChange={(e) => setNewTask({ ...newTask, time: e.target.value })}
                  placeholder="Ex: 15 min, 1h30" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Ficha de Origem</Label>
                <Input value={newTask.sheet} onChange={(e) => setNewTask({ ...newTask, sheet: e.target.value })}
                  placeholder="Nome da ficha" className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Dica (opcional)</Label>
              <Input value={newTask.tip} onChange={(e) => setNewTask({ ...newTask, tip: e.target.value })}
                placeholder="Dica profissional ou ponto de atenção" className="mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setAddTaskDialog(false); setEditingTask(null); }}>Cancelar</Button>
              <Button onClick={handleAddTask} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="save-task-btn">
                {editingTask ? "Salvar" : "Adicionar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      </>}
    </div>
  );
}
